import Badge from "@/common/components/Badge";
import GoBackButton from "@/common/components/GoBackButton";
import { SubdomainSection } from "@/common/components/SubdomainSection";
import Tabs from "@/common/components/Tabs";
import { useGoBack } from "@/common/hooks/useGoBack";
import { FormTransformers } from "@/common/utils/formTransformers";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import AccreditationSection from "../../components/GeneralData/AccreditationSection";
import BlockUnblockEstablishment from "../../components/GeneralData/BlockUnblockEstablishment";
import DisableEnableEstablishment from "../../components/GeneralData/DisableEnableEstablishment";
import GeneralDataSection from "../../components/GeneralData/GeneralDataSection";
import CreatorSectionUpdate from "../../components/Update/CreatorSection";
import { useUpdateEstablishmentCompany } from "../../hooks/useUpdateEstablishmentCompany";
import { updateEstablishmentCompanySchema, UpdateEstablishmentCompanyValidationSchema } from "../../schemas/updateEstablishmentCompany.schema";
import { updateEstablishmentCompanyDefaultValues } from "../../utils/updateEstablishmentCompanyDefaultValues";
import DisplayAndSalesRestrictionAccordion from "../../components/GeneralData/DisplayAndSalesRestrictions";

const EstablishmentCompanyUpdate = () => {
  const { id } = useParams();
  const goBack = useGoBack();

  const {
    data: establishmentCompany,
    isFetched: establishmentCompanyIsFetched,
  } = useEstablishmentCompany(id);

  const { mutate: updateEstablishmentCompany, isPending: updateEstablishmentCompanyIsPending } = useUpdateEstablishmentCompany({
    establishmentCompanyId: establishmentCompany?.id,
    establishmentCompany,
    onSuccess: () => {
      goBack({ fallback: `/establishments/${establishmentCompany?.id}` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: formMethods.setError })(error);
    },
  });

  const formMethods = useForm<UpdateEstablishmentCompanyValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanySchema),
    defaultValues: updateEstablishmentCompanyDefaultValues(establishmentCompany),
  });

  useEffect(() => {
    if (establishmentCompanyIsFetched) {
      formMethods.reset(updateEstablishmentCompanyDefaultValues(establishmentCompany));
    }
  }, [establishmentCompanyIsFetched]);

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyValidationSchema> = (form) => updateEstablishmentCompany(form);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <GoBackButton
            fallback={`/establishments`}
            searchLastRouteFromFallback
          />

          <h2 className="text-2xl font-bold">
            {establishmentCompany?.general_information?.fantasy_name}
          </h2>

          {establishmentCompany?.status === 'blocked' && (
            <Badge
              Icon={React.Fragment}
              text="Bloqueado"
            />
          )}

          {establishmentCompany?.status === 'disabled' && (
            <Badge
              Icon={React.Fragment}
              text="Desativado"
            />
          )}
        </div>
      </div>

      <Tabs
        tabsListClassName="w-full bg-transparent rounded-none"
        tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
        tabsContentClassName="mt-6"
        tabsList={[
          {
            label: "Dados Gerais",
            value: "",
            body: (
              <FormProvider {...formMethods}>
                <div className="flex flex-col bg-white-100 rounded-md center p-4 gap-6 overflow-y-auto max-h-[calc(100vh-192px)] w-full">
                  {establishmentCompany && (
                    <BlockUnblockEstablishment
                      establishment={establishmentCompany}
                      disableButtons
                    />
                  )}

                  {establishmentCompany && (
                    <DisableEnableEstablishment
                      establishment={establishmentCompany}
                      disableButtons
                    />
                  )}

                  <SubdomainSection url={establishmentCompany?.domains?.[0].host} />

                  <CreatorSectionUpdate
                    establishmentCompany={establishmentCompany}
                    onSubmit={onSubmit}
                    isUpdatePending={updateEstablishmentCompanyIsPending}
                  />

                  <DisplayAndSalesRestrictionAccordion
                    establishmentCompany={establishmentCompany}
                    disabled
                  />

                  <AccreditationSection
                    establishmentCompany={establishmentCompany}
                    disabled
                  />

                  <GeneralDataSection
                    establishmentCompany={establishmentCompany}
                    disabled
                  />
                </div>
              </FormProvider>
            ),
          },
          {
            label: "Produtos",
            value: "products",
            body: <></>,
            disabled: true,
          },
          {
            label: "Usuários",
            value: "users",
            body: <></>,
            disabled: true,
          },
          {
            label: "Transações",
            value: "events",
            body: <></>,
            disabled: true,
          },
          {
            label: "Totalizadores",
            value: "totals",
            body: <></>,
            disabled: true,
          },
          {
            label: "Faturas",
            value: "bills",
            body: <></>,
            disabled: true,
          },
        ]}
      />
    </div>
  );
};

export default EstablishmentCompanyUpdate;
